import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Quote Stage config — values must match Zoho EXACTLY ──────────────────────
// Higher-contrast/saturation versions of the original pale set, kept within
// the Encon palette (orange #ee7203 / black / clean neutral greys).
export const QUOTE_STAGES = [
  { value: 'Negotiation Tender',               bg: '#bfdbfe', text: '#1e3a8a', border: '#3b82f6' },
  { value: 'Negotiation Order to Place',       bg: '#ee7203', text: '#ffffff', border: '#c25c02' },
  { value: 'Awaiting Supplier Information',    bg: '#fde047', text: '#713f12', border: '#ca8a04' },
  { value: 'On Hold',                          bg: '#374151', text: '#ffffff', border: '#1f2937' },
  { value: 'Closed Won Order in Progress',     bg: '#4ade80', text: '#052e16', border: '#15803d' },
  { value: 'Closed Won Order Completed',       bg: '#047857', text: '#ffffff', border: '#065f46' },
  { value: 'Closed Lost',                      bg: '#dc2626', text: '#ffffff', border: '#991b1b' },
];
export const stageConfig = (stage) =>
  QUOTE_STAGES.find(s => s.value === stage) || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };

// ── QuoteStagePill — clickable pill that opens an inline dropdown to change stage ──
// Changing to/from a Closed stage auto-closes/reopens any Zoho Task(s) linked to the quote.
export function QuoteStagePill({ quoteId, stage, onStageChange, testIdPrefix = 'quote-stage' }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const cfg = stageConfig(stage);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.(`[data-testid="${testIdPrefix}-dropdown-${quoteId}"]`)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, quoteId, testIdPrefix]);

  const toggleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, left: r.right, width: r.width });
    }
    setOpen(v => !v);
  };

  const handleSelect = async (newStage) => {
    if (newStage === stage) { setOpen(false); return; }
    setOpen(false);
    setSaving(true);
    try {
      const res = await api.put(`${API}/zoho/quotes/${quoteId}/stage`, { stage: newStage });
      onStageChange(quoteId, newStage);
      const tasksUpdated = res?.data?.tasks_updated || 0;
      toast.success(tasksUpdated > 0
        ? `Stage updated to "${newStage}" — ${tasksUpdated} linked task${tasksUpdated > 1 ? 's' : ''} synced`
        : `Stage updated to "${newStage}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update stage');
    } finally { setSaving(false); }
  };

  return (
    <div ref={ref} className="relative flex-shrink-0" data-testid={`${testIdPrefix}-pill-${quoteId}`}>
      <button
        ref={btnRef}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleOpen(); }}
        disabled={saving}
        className="flex w-full items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all hover:opacity-80 active:scale-95"
        style={{ backgroundColor: cfg.bg, color: cfg.text, borderColor: cfg.border }}
        title="Click to change stage"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        <span className="max-w-[120px] truncate">{stage || 'No Stage'}</span>
        <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
      </button>
      {open && menuPos && createPortal(
        <div
          className="fixed z-[9999] bg-white rounded-xl shadow-xl border border-gray-200 py-1 min-w-[220px]"
          style={{ top: menuPos.top, left: menuPos.left - 220 }}
          data-testid={`${testIdPrefix}-dropdown-${quoteId}`}
          onClick={(e) => e.stopPropagation()}
        >
          {QUOTE_STAGES.map((s) => {
            const active = s.value === stage;
            return (
              <button
                key={s.value}
                onClick={() => handleSelect(s.value)}
                className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                data-testid={`${testIdPrefix}-option-${s.value.replace(/\s+/g, '-')}`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 border"
                  style={{ backgroundColor: s.bg, borderColor: s.border }}
                />
                <span className={`text-xs ${active ? 'font-bold' : 'font-medium'} text-gray-800`}>
                  {s.value}
                </span>
                {active && <ChevronRight className="w-3 h-3 ml-auto text-gray-400" />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
